import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Divider,
    Snackbar,
    Container,
    Alert, 
    MenuItem
} from '@mui/material';
import { Add, SaveAlt, Cancel, Edit } from '@mui/icons-material';
import ListaVehiculos from './ListaVehiculos';
import Lottie from 'lottie-react';
import registroVehiculos from './registro-vehiculo.json';

const RegistroVehiculos = () => {
    const [formData, setFormData] = useState({
        marca: '',
        modelo: '',
        año: '',
        tipo_combustible: '',
        placa: '',
        fecha_creacion: new Date().toISOString().split('T')[0], 
        kilometraje: '',
        estado: '',
        vin: '',
        fecha_adquisicion: '',
        color: '',
        descripcion: '',
        correlativo: ''
    });

    const [vehiculos, setVehiculos] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [editingVehiculo, setEditingVehiculo] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Función para cargar vehículos
    const fetchVehiculos = async () => {
        try {
            const response = await fetch('https://bei360.injupemp.gob.hn:8444/api/vehiculos/');
            if (!response.ok) {
                throw new Error('Error al obtener vehículos');
            }
            const data = await response.json();
            setVehiculos(data);
        } catch (error) {
            showSnackbar('Error al cargar vehículos: ' + error.message, 'error');
            console.error('Error:', error);
        }
    };

    // Cargar vehículos al montar el componente
    useEffect(() => {
        fetchVehiculos();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.marca) {
        showSnackbar('Por favor complete los campos requeridos', 'error');
        return;
    }

    try {
        // Preparar datos para enviar
        const datosEnvio = {
            marca: formData.marca,
            modelo: formData.modelo,
            año: formData.año.toString(),
            tipo_combustible: formData.tipo_combustible,
            placa: formData.placa && formData.placa.trim() !== "" ? formData.placa : null,
            fecha_creacion: formData.fecha_creacion,
            kilometraje: parseFloat(formData.kilometraje) || 0,
            estado: formData.estado,
            vin: formData.vin,
            fecha_adquisicion: formData.fecha_adquisicion,
            color: formData.color,
            descripcion: formData.descripcion,
            correlativo: formData.correlativo
        };

        console.log('Datos a enviar:', datosEnvio);
        console.log('Modo:', editingVehiculo ? 'Edición' : 'Creación');
        console.log('Vehículo a editar:', editingVehiculo);

        let response;
        
        if (editingVehiculo) {
            // Modo edición - Intentar primero con ID, luego con placa
            let urlPorId = `https://bei360.injupemp.gob.hn:8444/api/vehiculos/${editingVehiculo.id}/`;
            let urlPorPlaca = editingVehiculo.placa ? `https://bei360.injupemp.gob.hn:8444/api/vehiculos/${editingVehiculo.placa}/` : null;
            
            console.log('Intentando edición por ID:', urlPorId);
            
            // Primero intentar con ID
            response = await fetch(urlPorId, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(datosEnvio),
            });

            // Si falla por ID, intentar por placa
            if (!response.ok && urlPorPlaca) {
                console.log('Edición por ID falló, intentando por placa:', urlPorPlaca);
                response = await fetch(urlPorPlaca, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(datosEnvio),
                });
            }

            // Si PUT falla, intentar con PATCH
            if (!response.ok) {
                console.log('PUT falló, intentando con PATCH por ID...');
                response = await fetch(urlPorId, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(datosEnvio),
                });

                if (!response.ok && urlPorPlaca) {
                    console.log('PATCH por ID falló, intentando por placa...');
                    response = await fetch(urlPorPlaca, {
                        method: 'PATCH',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(datosEnvio),
                    });
                }
            }
        } else {
            // Modo creación - POST request
            response = await fetch('https://bei360.injupemp.gob.hn:8444/api/vehiculos/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(datosEnvio),
            });
        }

        if (!response.ok) {
            const errorData = await response.json();
            console.error('Detalles del error:', errorData);
            throw new Error(errorData.detail || `Error al ${editingVehiculo ? 'actualizar' : 'guardar'} vehículo`);
        }

        const vehiculoGuardado = await response.json();
        console.log('Vehículo guardado:', vehiculoGuardado);
        
        if (editingVehiculo) {
            // Actualizar el vehículo en la lista
            setVehiculos(vehiculos.map(v => v.id === editingVehiculo.id ? vehiculoGuardado : v));
            showSnackbar('Vehículo actualizado con éxito', 'success');
        } else {
            // Agregar nuevo vehículo a la lista
            setVehiculos([...vehiculos, vehiculoGuardado]);
            showSnackbar('Vehículo registrado con éxito', 'success');
        }

        handleCloseModal();
        fetchVehiculos();
        
    } catch (error) {
        console.error('Error completo:', error);
        showSnackbar(error.message, 'error');
    }
};

    const handleEdit = (vehiculo) => {
        setEditingVehiculo(vehiculo);
        setFormData({
            marca: vehiculo.marca || '',
            modelo: vehiculo.modelo || '',
            año: vehiculo.año || '',
            tipo_combustible: vehiculo.tipo_combustible || '',
            placa: vehiculo.placa || '',
            fecha_creacion: vehiculo.fecha_creacion || new Date().toISOString().split('T')[0],
            kilometraje: vehiculo.kilometraje || '',
            estado: vehiculo.estado || '',
            vin: vehiculo.vin || '',
            fecha_adquisicion: vehiculo.fecha_adquisicion || '',
            color: vehiculo.color || '',
            descripcion: vehiculo.descripcion || '',
            correlativo: vehiculo.correlativo || ''
        });
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setFormData({
            marca: '',
            modelo: '',
            año: '',
            tipo_combustible: '',
            placa: '',
            fecha_creacion: new Date().toISOString().split('T')[0],
            kilometraje: '',
            estado: '',
            vin: '',
            fecha_adquisicion: '',
            color: '',
            descripcion: '',
            correlativo: ''
        });
        setEditingVehiculo(null);
        setOpenModal(false);
    };

    const handleCancel = () => {
        handleCloseModal();
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const placaRegex = /^[A-Za-z]{3}\d{4}$/;

    return (
        <Container style={{ marginTop: 15 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h4" fontWeight="bold">Registro de vehículos</Typography>
            </div>
            <Box sx={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '200%' 
            }} >
                <Lottie
                    animationData={registroVehiculos}
                    loop={true}
                    autoplay={true}
                    style={{ width: 180, height: 180 }}
                />
            </Box>
            <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setOpenModal(true)}
                sx={{
                    mb: 1,
                    background: '#19438C'
                }}
            >
                Registrar Vehículo
            </Button>

            {/* Modal para el formulario */}
            <Dialog
                open={openModal}
                onClose={handleCancel}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        p: 2
                    }
                }}
            >
                <DialogTitle>
                    {editingVehiculo ? 'Editar vehículo' : 'Registrar nuevo vehículo'}
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit}>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 2 }}>
                            <TextField
                                label="Marca"
                                name="marca"
                                value={formData.marca}
                                onChange={handleChange}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Modelo"
                                name="modelo"
                                value={formData.modelo}
                                onChange={handleChange}
                                fullWidth
                                required
                            />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField
                                label="Año"
                                name="año"
                                value={formData.año}
                                onChange={handleChange}
                                fullWidth
                                required
                                inputProps={{
                                    min: 1900,
                                    max: new Date().getFullYear()
                                }}
                            />
                            <TextField
                                select
                                label="Tipo de combustible"
                                name="tipo_combustible"
                                value={formData.tipo_combustible}
                                onChange={handleChange}
                                fullWidth
                                required
                            >
                                <MenuItem value="Gasolina">GASOLINA</MenuItem>
                                <MenuItem value="Diesel">DIESEL</MenuItem>
                            </TextField>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField
                                label="Placa"
                                name="placa"
                                value={formData.placa}
                                onChange={(e) => {
                                    const value = e.target.value.toUpperCase();
                                    handleChange({ target: { name: e.target.name, value } });
                                }}
                                fullWidth
                                inputProps={{
                                    maxLength: 7,
                                    pattern: "[A-Za-z]{3}[0-9]{4}",
                                    title: "Formato: 3 letras seguidas de 4 números (ej: ABC1234)"
                                }}
                                error={formData.placa && !placaRegex.test(formData.placa)}
                                helperText={formData.placa && !placaRegex.test(formData.placa) 
                                    ? "Formato incorrecto: 3 letras + 4 números (ej: ABC1234)" 
                                    : ""}
                            />

                            <TextField
                                label="Fecha de adquisición"
                                name="fecha_adquisicion"
                                value={formData.fecha_adquisicion}
                                onChange={handleChange}
                                fullWidth
                                required
                                type="date"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField
                                label="VIN"
                                name="vin"
                                value={formData.vin}
                                onChange={handleChange}
                                fullWidth
                                required
                            />

                            <TextField
                                label="Kilometraje actual"
                                name="kilometraje"
                                value={formData.kilometraje}
                                onChange={handleChange}
                                fullWidth
                                required
                                sx={{ mb: 2 }}
                                inputProps={{ min: 0 }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField
                                label="Descripción"
                                name="descripcion"
                                value={formData.descripcion}
                                onChange={handleChange}
                                fullWidth
                                required
                            />

                            <TextField
                                label="Correlativo"
                                name="correlativo"
                                value={formData.correlativo}
                                onChange={handleChange}
                                fullWidth
                                required
                                sx={{ mb: 2 }}
                                inputProps={{ min: 0 }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField
                                label="Color"
                                name="color"
                                value={formData.color}
                                onChange={handleChange}
                                fullWidth
                                required
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                select
                                fullWidth
                                label="Estado"
                                name="estado"
                                value={formData.estado}
                                onChange={handleChange}
                                required
                            >
                                <MenuItem value="Activo">Activo</MenuItem>
                                <MenuItem value="Inactivo">Inactivo</MenuItem>
                            </TextField>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                            <Button
                                variant="outlined"
                                startIcon={<Cancel />}
                                onClick={handleCancel}
                                color="error"
                                sx={{
                                    minWidth: 120,
                                    color: '#FF0000'
                                }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={editingVehiculo ? <Edit /> : <SaveAlt />}
                                sx={{
                                    minWidth: 120,
                                    backgroundColor: '#19438C'
                                }}
                            >
                                {editingVehiculo ? 'Actualizar' : 'Guardar'}
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            <ListaVehiculos 
                vehiculos={vehiculos} 
                onEdit={handleEdit}
            />

            {/* Snackbar para notificaciones */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default RegistroVehiculos;